import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchUserAttributes } from "aws-amplify/auth";
import {
  KycReviewDetail,
  DOCUMENT_TYPE_LABELS,
  PlatformKycAPI,
} from "../../services/platformKyc.service";
import PlatformAdminNav from "../../components/platform/PlatformAdminNav";

const MAX_REJECTION_REASON_LENGTH = 500;

type DocMediaType = "pdf" | "image" | "unknown";

function getMediaType(url: string): DocMediaType {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    if (pathname.endsWith(".pdf")) return "pdf";
    if (/\.(jpe?g|png|gif|webp|bmp|tiff?)$/.test(pathname)) return "image";
  } catch {}
  return "unknown";
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) +
    " at " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZoneName: "short" })
  );
}

type RightPanelMode = "decision" | "rejecting" | "rejected" | "verified";

const KycReviewDetailPage: React.FC = () => {
  const { id: kycDocId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // doctor_id and item are threaded from the queue row via router state
  const doctorId: string = (location.state as any)?.doctor_id ?? "";
  const prefetched: KycReviewDetail | undefined = (location.state as any)?.item;

  const [detail, setDetail] = useState<KycReviewDetail | null>(prefetched ?? null);
  const [loading, setLoading] = useState(!prefetched);
  const [adminEmail, setAdminEmail] = useState<string>("");

  const [panelMode, setPanelMode] = useState<RightPanelMode>(
    prefetched?.status === "verified"
      ? "verified"
      : prefetched?.status === "rejected"
      ? "rejected"
      : "decision"
  );

  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchUserAttributes()
      .then(attrs => { if (!cancelled) setAdminEmail(attrs.email ?? ""); })
      .catch(() => {});
    if (!prefetched) setLoading(false);
    return () => { cancelled = true; };
  }, [prefetched]);

  const handleOpenDocument = () => {
    if (!detail?.view_url) {
      toast.error("Document URL not available. Please reload the queue.");
      return;
    }
    window.open(detail.view_url, "_blank", "noopener,noreferrer");
  };

  // Route verify/reject based on subject_kind.
  const isClinicDoc = detail?.subject_kind === "clinic";
  const isOnboardingDoc = detail?.subject_kind === "onboarding";

  const handleVerify = async () => {
    if (!kycDocId || !detail) return;
    if (!isClinicDoc && !isOnboardingDoc && !doctorId) return;
    setIsSubmitting(true);
    try {
      if (isOnboardingDoc) {
        await PlatformKycAPI.verifyOnboarding(kycDocId);
      } else if (isClinicDoc) {
        await PlatformKycAPI.verifyClinic(detail.clinic_id, kycDocId);
      } else {
        await PlatformKycAPI.verify(doctorId, kycDocId);
      }
      toast.success(
        <div>
          <p className="font-semibold">
            {isClinicDoc || isOnboardingDoc ? "Document verified" : `${detail.doctor_name} verified`}
          </p>
          <p className="text-xs">
            {isClinicDoc || isOnboardingDoc
              ? `${clinicDisplay} — document approved.`
              : "can_prescribe is now true. Doctor can prescribe."}
          </p>
        </div>,
        { autoClose: 4000 }
      );
      navigate("/platform/kyc", { replace: true });
    } catch {
      toast.error("Verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!kycDocId || !detail || !rejectionReason.trim()) return;
    if (!isClinicDoc && !isOnboardingDoc && !doctorId) return;
    setIsSubmitting(true);
    try {
      if (isOnboardingDoc) {
        await PlatformKycAPI.rejectOnboarding(kycDocId, rejectionReason.trim());
      } else if (isClinicDoc) {
        await PlatformKycAPI.rejectClinic(detail.clinic_id, kycDocId, rejectionReason.trim());
      } else {
        await PlatformKycAPI.reject(doctorId, kycDocId, rejectionReason.trim());
      }
      setDetail(prev =>
        prev
          ? {
              ...prev,
              status: "rejected",
              rejection_reason: rejectionReason.trim(),
              verified_by: adminEmail,
              verified_at: new Date().toISOString(),
            }
          : prev
      );
      setPanelMode("rejected");
      toast.error(
        <div>
          <p className="font-semibold">Submission rejected</p>
          <p className="text-xs">Clinic admin notified. Row removed from queue.</p>
        </div>,
        { autoClose: 4000 }
      );
    } catch {
      toast.error("Rejection failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const clinicDisplay = detail?.clinic_name ?? detail?.clinic_id ?? "";

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <PlatformAdminNav adminEmail={adminEmail} />

      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* Back link */}
        <button
          onClick={() => navigate("/platform/kyc")}
          className="text-[#0066cc] text-sm hover:underline mb-4 flex items-center gap-1"
        >
          ‹ Back to KYC Queue
        </button>

        {loading && (
          <div className="text-sm text-slate-500 py-12 text-center">Loading…</div>
        )}

        {!loading && detail && (
          <>
            {/* Doc header card */}
            <div className="bg-white rounded-xl border border-slate-200 px-6 py-4 mb-5 flex items-start justify-between shadow-sm">
              <div className="flex gap-5">
                <div className="bg-slate-50 border border-slate-100 rounded-lg px-4 py-3 min-w-[160px]">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Clinic</p>
                  <p className="text-[#0066cc] text-sm font-medium">{clinicDisplay}</p>
                  <p className="text-slate-900 font-bold mt-1">{detail.doctor_name}</p>
                </div>
                <div className="flex flex-col justify-center gap-0.5">
                  <p className="text-sm font-semibold text-slate-800">
                    Document: {DOCUMENT_TYPE_LABELS[detail.doc_type] ?? detail.doc_type}
                  </p>
                  <p className="text-sm text-slate-500">Submitted: {formatDate(detail.submitted_at)}</p>
                  <p className="text-xs text-slate-400">Doc ID: {detail.kyc_doc_id}</p>
                </div>
              </div>
              <StatusBadge status={detail.status} />
            </div>

            {/* Post-reject banner */}
            {panelMode === "rejected" && (
              <div className="flex items-center gap-3 rounded-xl bg-red-50 border border-red-200 px-5 py-3 mb-5">
                <span className="flex-shrink-0 w-3 h-3 rounded-full bg-red-500" />
                <p className="text-sm text-red-800 font-medium">
                  Submission rejected. The clinic admin at {clinicDisplay} has been notified with the rejection reason.
                </p>
              </div>
            )}

            {/* Main layout */}
            <div className="flex gap-5">
              {/* Left — Document preview */}
              <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Document Preview</p>
                  <button
                    onClick={handleOpenDocument}
                    className="rounded-lg bg-[#0066ff] text-white text-sm font-medium px-4 py-2 hover:bg-[#0051cc] transition-colors"
                  >
                    Open document
                  </button>
                </div>
                {detail?.view_url ? (
                  (() => {
                    const mediaType = getMediaType(detail.view_url);
                    if (mediaType === "image") {
                      return (
                        <div className="flex-1 min-h-[400px] rounded-lg bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center">
                          <img
                            src={detail.view_url}
                            alt="Document"
                            className="max-w-full max-h-[600px] object-contain"
                          />
                        </div>
                      );
                    }
                    // PDF or unknown — use iframe (browsers render PDFs natively)
                    return (
                      <iframe
                        src={detail.view_url}
                        title="Document preview"
                        className="flex-1 w-full min-h-[500px] rounded-lg border border-slate-200"
                        style={{ height: 500 }}
                      />
                    );
                  })()
                ) : (
                  <div className="flex-1 min-h-[400px] rounded-lg bg-slate-50 border border-slate-200 flex flex-col items-center justify-center gap-2">
                    <svg viewBox="0 0 24 24" className="w-10 h-10 fill-slate-300">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                    </svg>
                    <p className="text-xs text-red-500 font-medium">No document URL — go back and re-open this row.</p>
                  </div>
                )}
              </div>

              {/* Right — Review panel */}
              <div className="w-80 flex-shrink-0">
                {panelMode === "decision" && (
                  <DecisionPanel
                    detail={detail}
                    clinicDisplay={clinicDisplay}
                    isSubmitting={isSubmitting}
                    onVerify={handleVerify}
                    onStartReject={() => setPanelMode("rejecting")}
                  />
                )}
                {panelMode === "rejecting" && (
                  <RejectPanel
                    clinicDisplay={clinicDisplay}
                    reason={rejectionReason}
                    onReasonChange={setRejectionReason}
                    isSubmitting={isSubmitting}
                    onCancel={() => { setPanelMode("decision"); setRejectionReason(""); }}
                    onConfirm={handleReject}
                  />
                )}
                {panelMode === "rejected" && detail.rejection_reason && (
                  <RejectedPanel
                    reason={detail.rejection_reason}
                    rejectedBy={detail.verified_by ?? adminEmail}
                    rejectedAt={detail.verified_at ? formatDateTime(detail.verified_at) : ""}
                    onBackToQueue={() => navigate("/platform/kyc")}
                  />
                )}
                {panelMode === "verified" && (
                  <VerifiedPanel
                    detail={detail}
                    onBackToQueue={() => navigate("/platform/kyc")}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Sub-panels ────────────────────────────────────────────────────────────────

const DecisionPanel: React.FC<{
  detail: KycReviewDetail;
  clinicDisplay: string;
  isSubmitting: boolean;
  onVerify: () => void;
  onStartReject: () => void;
}> = ({ detail, clinicDisplay, isSubmitting, onVerify, onStartReject }) => {
  const isClinicDoc = detail.subject_kind === "clinic";
  const isOnboardingDoc = detail.subject_kind === "onboarding";
  return (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
    <h2 className="font-semibold text-slate-800">Review Decision</h2>

    {/* Info box */}
    <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 space-y-0.5">
      <p className="text-xs text-blue-700 font-medium">Cross-clinic review: {clinicDisplay}</p>
      <p className="text-xs text-blue-600">
        {isOnboardingDoc
          ? "Verifying approves this onboarding business document."
          : isClinicDoc
          ? "Verifying approves this clinic credentialing document."
          : "Verifying sets can_prescribe = true for this doctor."}
      </p>
      <p className="text-xs text-blue-600">This action is permanent and audited.</p>
    </div>

    {/* Verify */}
    <div>
      <button
        onClick={onVerify}
        disabled={isSubmitting}
        className="w-full rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold text-sm py-3 transition-colors"
      >
        Verify — Approve this document
      </button>
      <p className="text-xs text-green-700 mt-1 text-center">
        {isClinicDoc ? `Approves credential for ${clinicDisplay}` : `Enables prescribing for ${detail.doctor_name}`}
      </p>
    </div>

    {/* Reject */}
    <div>
      <button
        onClick={onStartReject}
        disabled={isSubmitting}
        className="w-full rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold text-sm py-3 transition-colors"
      >
        Reject — Document not acceptable
      </button>
      <p className="text-xs text-red-600 mt-1 text-center">
        You will be prompted for a mandatory reason
      </p>
    </div>

    {/* Other docs */}
    {detail.other_docs && detail.other_docs.length > 0 && (
      <div className="pt-2 border-t border-slate-100">
        <p className="text-xs font-semibold text-slate-600 mb-2">Other docs from this doctor</p>
        <div className="space-y-1">
          {detail.other_docs.map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-slate-600">{DOCUMENT_TYPE_LABELS[d.doc_type] ?? d.doc_type}</span>
              <span className={d.status === "verified" ? "text-green-600" : d.status === "rejected" ? "text-red-500" : "text-yellow-700"}>
                — {d.status === "verified" ? "Verified" : d.status === "rejected" ? "Rejected" : "Pending Review"}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
  );
};

const RejectPanel: React.FC<{
  clinicDisplay: string;
  reason: string;
  onReasonChange: (v: string) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ clinicDisplay, reason, onReasonChange, isSubmitting, onCancel, onConfirm }) => {
  const hasReason = reason.trim().length > 0;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
      <div>
        <h2 className="font-semibold text-slate-800">Reject this document</h2>
        <p className="text-xs text-slate-500 mt-0.5">A reason is required and will be shared with the clinic admin.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Rejection reason <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reason}
          onChange={e => onReasonChange(e.target.value.slice(0, MAX_REJECTION_REASON_LENGTH))}
          placeholder="Describe why the document is not acceptable…"
          rows={5}
          className={`w-full rounded-lg border px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none resize-none transition-colors ${
            hasReason
              ? "border-[#0066ff] ring-1 ring-[#0066ff]"
              : "border-slate-200"
          }`}
        />
        {hasReason ? (
          <p className="text-xs text-slate-400 mt-1">{reason.trim().length} / {MAX_REJECTION_REASON_LENGTH} characters</p>
        ) : (
          <p className="text-xs text-slate-400 mt-1">* A reason must be entered to enable rejection</p>
        )}
      </div>

      {hasReason && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-3 py-2">
          <p className="text-xs text-yellow-800">Clinic admin at {clinicDisplay} will be notified by email.</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium py-2.5 hover:bg-slate-50 transition-colors disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!hasReason || isSubmitting}
          className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 transition-colors"
        >
          {isSubmitting ? "Rejecting…" : "Reject document"}
        </button>
      </div>
      {!hasReason && (
        <p className="text-xs text-slate-400 text-center -mt-2">Enter reason above to enable this button</p>
      )}
    </div>
  );
};

const RejectedPanel: React.FC<{
  reason: string;
  rejectedBy: string;
  rejectedAt: string;
  onBackToQueue: () => void;
}> = ({ reason, rejectedBy, rejectedAt, onBackToQueue }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
    <h2 className="font-semibold text-slate-800">Document Rejected</h2>
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1">Rejection reason</p>
      <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-3">
        <p className="text-sm text-red-800">{reason}</p>
      </div>
    </div>
    <div className="space-y-0.5 text-xs text-slate-500">
      {rejectedBy && <p>Rejected by: {rejectedBy}</p>}
      {rejectedAt && <p>Rejected on: {rejectedAt}</p>}
    </div>
    <p className="text-xs text-slate-400">Removed from pending queue. Clinic admin email sent.</p>
    <button
      onClick={onBackToQueue}
      className="w-full rounded-xl border border-slate-200 text-slate-700 text-sm font-medium py-2.5 hover:bg-slate-50 transition-colors"
    >
      Back to Queue
    </button>
  </div>
);

const VerifiedPanel: React.FC<{
  detail: KycReviewDetail;
  onBackToQueue: () => void;
}> = ({ detail, onBackToQueue }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4">
    <h2 className="font-semibold text-slate-800">Document Verified</h2>
    <div className="rounded-lg bg-green-50 border border-green-100 px-3 py-3">
      <p className="text-sm text-green-800">
        This document has been verified. {detail.doctor_name} can prescribe.
      </p>
    </div>
    <div className="space-y-0.5 text-xs text-slate-500">
      {detail.verified_by && <p>Verified by: {detail.verified_by}</p>}
      {detail.verified_at && <p>Verified on: {formatDateTime(detail.verified_at)}</p>}
    </div>
    <button
      onClick={onBackToQueue}
      className="w-full rounded-xl border border-slate-200 text-slate-700 text-sm font-medium py-2.5 hover:bg-slate-50 transition-colors"
    >
      Back to Queue
    </button>
  </div>
);

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  if (status === "submitted") {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs font-semibold px-3 py-1">
        Pending Review
      </span>
    );
  }
  if (status === "verified") {
    return (
      <span className="inline-flex items-center rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-3 py-1">
        Verified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-semibold px-3 py-1">
      Rejected
    </span>
  );
};

export default KycReviewDetailPage;
